// Regras de visitação do PNPG.
//
// Fontes:
// - Resolução do Conselho de Ministros n.º 11-A/2011, de 4 de fevereiro
// - Tabela informativa "Atividades de Turismo de Natureza — Áreas sujeitas a
//   regimes de proteção", fornecida pelo ICNF (ficheiro ZPpnpg.pdf)
//
// A tabela do ICNF declara expressamente que é informativa e não substitui o
// Plano de Ordenamento. Por isso, resultados "permitidos" continuam a incluir
// o aviso de confirmação de eventuais restrições temporárias junto do PNPG.

export const PNPG_AREA_ID = "pnpg";

export const PNPG_ZONE_TYPES = Object.freeze({
  TOTAL: "total",
  PARTIAL_I: "partial_i",
  PARTIAL_II: "partial_ii",
  COMPLEMENTARY_I: "complementary_i",
  COMPLEMENTARY_II: "complementary_ii",
  UNPROTECTED: "unprotected",
});

export const PNPG_RULE_STATUS = Object.freeze({
  ALLOWED: "allowed",
  AUTHORIZATION_REQUIRED: "authorization_required",
  PROHIBITED: "prohibited",
  NOT_APPLICABLE: "not_applicable",
  NEEDS_CONFIRMATION: "needs_confirmation",
});

export const PNPG_ACTIVITIES = Object.freeze({
  WALKING: "walking",
  NATURE_OBSERVATION: "nature_observation",
  ORIENTEERING: "orienteering",
  CLIMBING_RAPPEL: "climbing_rappel",
  CANYONING: "canyoning",
  ROPE_COURSE: "rope_course",
  TARGET_SPORTS: "target_sports",
  CYCLING: "cycling",
  MOUNTAIN_BIKING: "mountain_biking",
  EQUESTRIAN: "equestrian",
  MOTORIZED_OFF_ROAD: "motorized_off_road",
  MOTORIZED_BOAT: "motorized_boat",
  NON_MOTORIZED_BOAT: "non_motorized_boat",
  WATER_SPORTS: "water_sports",
  NON_MOTORIZED_FLIGHT: "non_motorized_flight",
  MOTORIZED_AIRCRAFT: "motorized_aircraft",
});

export const PNPG_CONDITIONS = Object.freeze({
  EXISTING_TRAILS_ONLY: "existing_trails_only",
  AUTHORIZED_LOCATIONS_ONLY: "authorized_locations_only",
  EXISTING_EQUIPMENT_ONLY: "existing_equipment_only",
  NEW_LOCATIONS_REQUIRE_AUTHORIZATION: "new_locations_require_authorization",
  NON_COMPETITIVE_ONLY: "non_competitive_only",
  AUTHORIZED_RESERVOIRS_ONLY: "authorized_reservoirs_only",
  CHECK_TEMPORARY_RESTRICTIONS: "check_temporary_restrictions",
  COMMUNICATE_VISIT: "communicate_visit",
});

export const PNPG_SOURCE = Object.freeze({
  regulation:
    "Resolução do Conselho de Ministros n.º 11-A/2011, de 4 de fevereiro",
  regulationUrl:
    "https://diariodarepublica.pt/dr/detalhe/resolucao-conselho-ministros/11-a-2011-337242",
  tableTitle:
    "Atividades de Turismo de Natureza — Áreas sujeitas a regimes de proteção",
  contactEmail: "pnpg@icnf.pt",
  checkedAt: "2026-10-24",
  disclaimer:
    "A tabela é meramente informativa e não substitui a consulta do Plano de Ordenamento nem a confirmação de interdições temporárias junto do PNPG.",
});

const C = PNPG_CONDITIONS;
const S = PNPG_RULE_STATUS;

const rule = (status, conditions = [], note = "") =>
  Object.freeze({ status, conditions: Object.freeze(conditions), note });

const allowed = (conditions = []) => rule(S.ALLOWED, conditions);
const auth = (conditions = [], note = "") =>
  rule(S.AUTHORIZATION_REQUIRED, conditions, note);
const prohibited = () => rule(S.PROHIBITED);
const notApplicable = () => rule(S.NOT_APPLICABLE);

const existingTrail = [C.EXISTING_TRAILS_ONLY, C.AUTHORIZED_LOCATIONS_ONLY];
const complementaryAllowed = [C.CHECK_TEMPORARY_RESTRICTIONS, C.COMMUNICATE_VISIT];

// O asterisco da tabela aplica a autorização a grupos com mais de 10 pessoas.
const partialIThresholdRule = Object.freeze({
  kind: "participant_threshold",
  operator: ">",
  participants: 10,
  whenTrue: auth(existingTrail, "Nota * da tabela do ICNF."),
  whenFalse: allowed(existingTrail),
});

// A nota ** do PDF fornecido diz literalmente "menos de 15 elementos". Como
// a própria célula já diz "carece de autorização" e o artigo 8.º abrange
// grupos com mais de 15, o resultado seguro é autorização em qualquer dimensão
// de grupo. Mantemos o texto original para auditoria, sem o corrigir por palpite.
const partialIIAuthorization = auth(
  existingTrail,
  'A nota ** do documento fornecido refere literalmente "grupos com menos de 15 elementos"; grupos maiores podem ainda estar abrangidos pelo artigo 8.º.'
);

const complementaryFacilityRule = allowed([
  C.EXISTING_EQUIPMENT_ONLY,
  C.NEW_LOCATIONS_REQUIRE_AUTHORIZATION,
  ...complementaryAllowed,
]);

export const PNPG_RULES = Object.freeze({
  [PNPG_ACTIVITIES.WALKING]: Object.freeze({
    total: auth(existingTrail),
    partial_i: partialIThresholdRule,
    partial_ii: partialIIAuthorization,
    complementary_i: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
    complementary_ii: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
  }),
  [PNPG_ACTIVITIES.NATURE_OBSERVATION]: Object.freeze({
    total: auth(existingTrail),
    partial_i: partialIThresholdRule,
    partial_ii: partialIIAuthorization,
    complementary_i: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
    complementary_ii: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
  }),
  [PNPG_ACTIVITIES.ORIENTEERING]: Object.freeze({
    total: prohibited(),
    partial_i: prohibited(),
    partial_ii: auth(existingTrail),
    complementary_i: auth([C.AUTHORIZED_LOCATIONS_ONLY]),
    complementary_ii: auth([C.AUTHORIZED_LOCATIONS_ONLY]),
  }),
  [PNPG_ACTIVITIES.CLIMBING_RAPPEL]: Object.freeze({
    total: prohibited(),
    partial_i: partialIThresholdRule,
    partial_ii: partialIIAuthorization,
    complementary_i: allowed([C.AUTHORIZED_LOCATIONS_ONLY, ...complementaryAllowed]),
    complementary_ii: allowed([C.AUTHORIZED_LOCATIONS_ONLY, ...complementaryAllowed]),
  }),
  [PNPG_ACTIVITIES.CANYONING]: Object.freeze({
    total: prohibited(),
    partial_i: prohibited(),
    partial_ii: partialIIAuthorization,
    complementary_i: allowed([C.AUTHORIZED_LOCATIONS_ONLY, ...complementaryAllowed]),
    complementary_ii: allowed([C.AUTHORIZED_LOCATIONS_ONLY, ...complementaryAllowed]),
  }),
  [PNPG_ACTIVITIES.ROPE_COURSE]: Object.freeze({
    total: prohibited(),
    partial_i: prohibited(),
    partial_ii: prohibited(),
    complementary_i: complementaryFacilityRule,
    complementary_ii: complementaryFacilityRule,
  }),
  [PNPG_ACTIVITIES.TARGET_SPORTS]: Object.freeze({
    total: prohibited(),
    partial_i: prohibited(),
    partial_ii: prohibited(),
    complementary_i: complementaryFacilityRule,
    complementary_ii: complementaryFacilityRule,
  }),
  [PNPG_ACTIVITIES.CYCLING]: Object.freeze({
    total: prohibited(),
    partial_i: partialIThresholdRule,
    partial_ii: partialIIAuthorization,
    complementary_i: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
    complementary_ii: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
  }),
  [PNPG_ACTIVITIES.MOUNTAIN_BIKING]: Object.freeze({
    total: prohibited(),
    partial_i: prohibited(),
    partial_ii: partialIIAuthorization,
    complementary_i: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
    complementary_ii: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
  }),
  [PNPG_ACTIVITIES.EQUESTRIAN]: Object.freeze({
    total: prohibited(),
    partial_i: partialIThresholdRule,
    partial_ii: partialIIAuthorization,
    complementary_i: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
    complementary_ii: allowed([C.EXISTING_TRAILS_ONLY, ...complementaryAllowed]),
  }),
  [PNPG_ACTIVITIES.MOTORIZED_OFF_ROAD]: Object.freeze({
    total: prohibited(),
    partial_i: prohibited(),
    partial_ii: prohibited(),
    complementary_i: auth([C.NON_COMPETITIVE_ONLY, C.AUTHORIZED_LOCATIONS_ONLY]),
    complementary_ii: auth([C.NON_COMPETITIVE_ONLY, C.AUTHORIZED_LOCATIONS_ONLY]),
  }),
  [PNPG_ACTIVITIES.MOTORIZED_BOAT]: Object.freeze({
    total: notApplicable(),
    partial_i: prohibited(),
    partial_ii: notApplicable(),
    complementary_i: auth([C.AUTHORIZED_RESERVOIRS_ONLY]),
    complementary_ii: auth([C.AUTHORIZED_RESERVOIRS_ONLY]),
  }),
  [PNPG_ACTIVITIES.NON_MOTORIZED_BOAT]: Object.freeze({
    total: notApplicable(),
    partial_i: prohibited(),
    partial_ii: notApplicable(),
    complementary_i: auth([C.AUTHORIZED_LOCATIONS_ONLY]),
    complementary_ii: auth([C.AUTHORIZED_LOCATIONS_ONLY]),
  }),
  [PNPG_ACTIVITIES.WATER_SPORTS]: Object.freeze({
    total: notApplicable(),
    partial_i: prohibited(),
    partial_ii: notApplicable(),
    complementary_i: auth([C.AUTHORIZED_LOCATIONS_ONLY]),
    complementary_ii: auth([C.AUTHORIZED_LOCATIONS_ONLY]),
  }),
  [PNPG_ACTIVITIES.NON_MOTORIZED_FLIGHT]: Object.freeze({
    total: prohibited(),
    partial_i: prohibited(),
    partial_ii: prohibited(),
    complementary_i: auth([C.AUTHORIZED_LOCATIONS_ONLY]),
    complementary_ii: auth([C.AUTHORIZED_LOCATIONS_ONLY]),
  }),
  [PNPG_ACTIVITIES.MOTORIZED_AIRCRAFT]: Object.freeze({
    total: prohibited(),
    partial_i: prohibited(),
    partial_ii: prohibited(),
    complementary_i: prohibited(),
    complementary_ii: prohibited(),
  }),
});

